import { Card, Container, Heading, Text } from '@nrx/ui';
import type { JSX } from 'react';
import { Navigate, Route, Routes } from 'react-router';

import ConsultSchedulingPage from './features/consult-scheduling-page.js';
import { ConsultErrorBoundary } from './features/error-boundary/consult-error-boundary.js';
import { ConsultLayout } from './features/layout/consult-layout.js';
import WelcomePage from './features/welcome/welcome-page.js';

/**
 * Route tree. `ConsultLayout` wraps the whole tree rather than each element, so every route —
 * including the loading and exchange-failed states of `/consult-scheduling/:token`, which used to
 * render bare — sits inside the shared shell.
 *
 * `ConsultErrorBoundary` sits INSIDE the layout and around the routed content: a render-time throw
 * anywhere in a page degrades to a visible error surface with the brand header and footer still
 * standing, instead of unmounting the tree to an empty `<body>`. Outside the layout it would take
 * the chrome down with the content, which is the symptom it exists to prevent.
 */
/**
 * Reached only by visiting the apex with no token — a mistyped or truncated link.
 *
 * It was `<div className="p-8 text-center text-gray-600">`, the single ad-hoc-Tailwind, non-token
 * surface in an app whose `index.css` otherwise holds zero raw colour literals. `text-gray-600` is
 * not a brand token, so it did not track the theme and would not have followed a palette change.
 * Now the same `Container` / `Card` / `Heading` / `Text` composition as every other terminal state
 * in this app, which also gives the route the `h1` it had no heading for at all.
 */
function InvalidLink(): JSX.Element {
  return (
    <Container as="section" size="4" pt="8" pb="10">
      <Card elevation="soft" className="consult-panel consult-panel--narrow">
        <Heading as="h1" size="7" className="consult-panel__title">
          Invalid link
        </Heading>
        <Text as="p" color="muted">
          This link is missing its scheduling code. Please open the most recent link we sent you.
        </Text>
      </Card>
    </Container>
  );
}

function App(): JSX.Element {
  return (
    <ConsultLayout>
      <ConsultErrorBoundary>
        <Routes>
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/consult-scheduling/:token" element={<ConsultSchedulingPage />} />
          <Route
            path="/"
            element={
              import.meta.env.DEV ? (
                <Navigate to="/consult-scheduling/local-dev" replace />
              ) : (
                <InvalidLink />
              )
            }
          />
        </Routes>
      </ConsultErrorBoundary>
    </ConsultLayout>
  );
}

export default App;
